import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { translationStatuses, type CompareResult, type LandingDraft, type SeoReport, type VersionSummary } from '@agency/contracts';
import { Badge, Button, Drawer, EmptyState, Notice, SelectField, Skeleton, TextField, useToast } from '@agency/ui';
import { ApiClientError, get, patch, post } from '../../lib/api.ts';
import { fmtDate } from '../../lib/format.ts';
import { useAuth } from '../../lib/auth.tsx';
import { QueryError } from '../../lib/query-state.tsx';

const CHANGE_TONE = { added: 'success', removed: 'error', changed: 'warning', moved: 'info', unchanged: 'neutral' } as const;
const STATUS_LABEL: Record<string, string> = { NOT_STARTED: 'Not started', DRAFT: 'Draft', NEEDS_REVIEW: 'Needs review', APPROVED: 'Approved', PUBLISHED: 'Published' };

function Diff({ variantId, from, to }: { variantId: string; from: string; to: string }) {
  const q = useQuery<CompareResult, ApiClientError>({ queryKey: ['compare', variantId, from, to], queryFn: () => get(`/admin/landings/${variantId}/compare?from=${from}&to=${to}`) });
  if (q.isLoading) return <div role="status"><span className="ag-visually-hidden">Comparing…</span><Skeleton height={80} /></div>;
  if (q.isError) return <QueryError error={q.error} onRetry={() => void q.refetch()} />;
  const r = q.data!;
  if (r.identical) return <Notice tone="info" live={false} title="No differences">{r.from.label} and {r.to.label} have the same content.</Notice>;
  return (
    <div>
      <p className="ag-hint">{r.from.label} → {r.to.label}</p>
      <ul className="diff-list" aria-label="Changes">
        {r.seoFields.length ? <li><Badge tone="warning">Changed</Badge> Page and SEO: {r.seoFields.join(', ')}</li> : null}
        {r.blocks.filter((b) => b.change !== 'unchanged').map((b) => (
          <li key={b.id}><Badge tone={CHANGE_TONE[b.change]}>{b.change[0]!.toUpperCase() + b.change.slice(1)}</Badge> {b.type}{b.fields.length ? `: ${b.fields.join(', ')}` : ''}</li>
        ))}
      </ul>
    </div>
  );
}

/** Immutable history: compare any version with the draft and restore it as a new draft (history is never rewritten). */
export function VersionsDrawer({ open, onClose, variantId, revision, canEdit, dirty, onRestored }: { open: boolean; onClose: () => void; variantId: string; revision: number; canEdit: boolean; dirty: boolean; onRestored: () => void }) {
  const toast = useToast();
  const [picked, setPicked] = useState<number | null>(null);
  const q = useQuery<VersionSummary[], ApiClientError>({ queryKey: ['versions', variantId], queryFn: () => get(`/admin/landings/${variantId}/versions`), enabled: open });
  const restore = useMutation<unknown, ApiClientError, number>({
    mutationFn: (n) => post(`/admin/landings/${variantId}/versions/${n}/restore`, { expectedRevision: revision }),
    onSuccess: (_r, n) => { toast({ tone: 'success', message: `Version ${n} restored as the current draft. Review it, then publish as a new version.` }); setPicked(null); onRestored(); onClose(); },
  });
  return (
    <Drawer open={open} onClose={onClose} title="Version history" width={560}>
      {!open ? null : q.isLoading || !q.data && !q.isError ? <div role="status"><span className="ag-visually-hidden">Loading versions…</span><Skeleton height={120} /></div> : q.isError ? <QueryError error={q.error} onRetry={() => void q.refetch()} /> : q.data!.length === 0 ? (
        <EmptyState title="Never published" icon="clock">Each publish creates an immutable version that appears here.</EmptyState>
      ) : (
        <ul className="seo-list" aria-label="Versions">
          {q.data!.map((v) => (
            <li key={v.versionId} style={{ gridTemplateColumns: '1fr' }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <strong>Version {v.versionNumber}</strong>
                {v.isLive ? <Badge tone="success">Live</Badge> : null}
                <span className="ag-hint">{fmtDate(v.publishedAt)}{v.publishedByName ? ` by ${v.publishedByName}` : ''}</span>
                <span style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                  <Button size="sm" aria-pressed={picked === v.versionNumber} onClick={() => setPicked(picked === v.versionNumber ? null : v.versionNumber)}>{picked === v.versionNumber ? 'Hide changes' : 'Compare with draft'}</Button>
                  {canEdit ? <Button size="sm" loading={restore.isPending && restore.variables === v.versionNumber} disabled={dirty} title={dirty ? 'Wait for your changes to save first' : undefined} onClick={() => restore.mutate(v.versionNumber)}>Restore</Button> : null}
                </span>
              </div>
              {v.changeSummary ? <p style={{ margin: '4px 0 0' }}>{v.changeSummary}</p> : null}
              <div className="ag-hint ag-mono">/{v.slug}</div>
              {picked === v.versionNumber ? <div style={{ marginTop: 8 }}><Diff variantId={variantId} from={String(v.versionNumber)} to="draft" /></div> : null}
            </li>
          ))}
        </ul>
      )}
      {restore.isError ? <div style={{ marginTop: 12 }}><Notice tone="error" title="Could not restore">{restore.error.message}</Notice></div> : null}
    </Drawer>
  );
}

/** Actionable checks for the saved draft. Blockers stop publishing; warnings do not. There is deliberately no score. */
export function ChecksDrawer({ open, onClose, variantId, savedAt }: { open: boolean; onClose: () => void; variantId: string; savedAt: number }) {
  const q = useQuery<SeoReport, ApiClientError>({ queryKey: ['seo-check', variantId, savedAt], queryFn: () => get(`/admin/landings/${variantId}/seo-check`), enabled: open });
  const r = q.data;
  return (
    <Drawer open={open} onClose={onClose} title="SEO and publishing checks" width={560}>
      {q.isLoading ? <div role="status"><span className="ag-visually-hidden">Running checks…</span><Skeleton height={160} /></div> : q.isError ? <QueryError error={q.error} onRetry={() => void q.refetch()} /> : r ? (
        <>
          <p role="status">{r.blockers === 0 ? 'No blockers.' : `${r.blockers} blocker${r.blockers === 1 ? '' : 's'} must be fixed before publishing.`} {r.warnings} warning{r.warnings === 1 ? '' : 's'}. Checks run on the saved draft.</p>
          <ul className="seo-list" aria-label="Checks">
            {[...r.checks].sort((a, b) => ({ fail: 0, warn: 1, pass: 2 })[a.status] - ({ fail: 0, warn: 1, pass: 2 })[b.status]).map((c) => (
              <li key={c.key}>
                <Badge tone={c.status === 'pass' ? 'success' : c.status === 'warn' ? 'warning' : 'error'}>{c.status === 'pass' ? 'Pass' : c.status === 'warn' ? 'Warning' : c.blocker ? 'Blocker' : 'Fail'}</Badge>
                <span><strong>{c.label}</strong><br /><span className="ag-hint">{c.detail}</span></span>
              </li>
            ))}
          </ul>
          <dl className="summary" style={{ marginTop: 16 }}>
            <dt>Canonical URL</dt><dd className="ag-mono" style={{ wordBreak: 'break-all' }}>{r.canonical}</dd>
            <dt>Structured data</dt><dd>{r.jsonLdTypes.length ? r.jsonLdTypes.join(', ') : 'None'}</dd>
          </dl>
        </>
      ) : null}
    </Drawer>
  );
}

/** Schedule publication. The server re-runs the publish checks now, so a schedule that would fail is refused immediately. */
export function ScheduleDrawer({ open, onClose, variantId, revision, onDone }: { open: boolean; onClose: () => void; variantId: string; revision: number; onDone: () => void }) {
  const toast = useToast();
  const [when, setWhen] = useState('');
  const m = useMutation<unknown, ApiClientError, void>({
    mutationFn: () => post(`/admin/landings/${variantId}/schedule`, { at: new Date(when).toISOString(), expectedRevision: revision }),
    onSuccess: () => { toast({ tone: 'success', message: 'Publication scheduled. The page stays as it is until then.' }); onDone(); onClose(); },
  });
  useEffect(() => { if (!open) { setWhen(''); m.reset(); } }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <Drawer open={open} onClose={onClose} title="Schedule publication"
      footer={<><Button onClick={onClose}>Cancel</Button><Button variant="primary" loading={m.isPending} disabled={!when} onClick={() => m.mutate()}>Schedule</Button></>}>
      <p className="ag-hint">The saved draft (revision {revision}) is published at the chosen time in your local time zone ({Intl.DateTimeFormat().resolvedOptions().timeZone}). Editing the draft afterwards does not change what is scheduled.</p>
      <TextField label="Publish at" type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} data-autofocus error={m.error?.fieldErrors.find((f) => f.field === 'at')?.message} />
      {m.isError ? <div style={{ marginTop: 12 }}><Notice tone="error" title="Could not schedule">{m.error.message}{m.error.fieldErrors.length ? <ul>{m.error.fieldErrors.map((f, i) => <li key={i}>{f.message}</li>)}</ul> : null}</Notice></div> : null}
    </Drawer>
  );
}

/** Name, URL path and translation status (with the sibling languages of this page). Changing the path of a live page creates a 301 automatically. */
export function PageSettings({ draft, canEdit, onChanged }: { draft: LandingDraft; canEdit: boolean; onChanged: () => void }) {
  const { can } = useAuth();
  const toast = useToast();
  const qc = useQueryClient();
  const [name, setName] = useState(draft.name);
  const [slug, setSlug] = useState(draft.slug);
  useEffect(() => { setName(draft.name); setSlug(draft.slug); }, [draft.variantId, draft.name, draft.slug]);
  const m = useMutation<unknown, ApiClientError, Record<string, unknown>>({
    mutationFn: (body) => patch(`/admin/landings/${draft.variantId}`, body),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['landings'] }); onChanged(); toast({ tone: 'success', message: 'Page settings saved.' }); },
  });
  const changed = name.trim() !== draft.name || slug !== draft.slug;
  const err = (f: string) => m.error?.fieldErrors.find((x) => x.field === f)?.message;
  return (
    <div style={{ display: 'grid', gap: 12, marginTop: 8, paddingTop: 12, borderTop: '1px solid var(--c-divider)' }}>
      <h3 style={{ margin: 0 }}>Page settings</h3>
      <TextField label="Internal name" value={name} readOnly={!canEdit} onChange={(e) => setName(e.target.value)} error={err('name')} />
      <TextField label="URL path" className="ag-mono" value={slug} readOnly={!canEdit} onChange={(e) => setSlug(e.target.value)} error={err('slug')}
        hint={draft.publishedVersionNumber !== null ? 'This page is live. Changing the path keeps the old address working with a permanent redirect.' : 'Language prefix is added automatically.'} />
      {canEdit ? <div><Button size="sm" variant="primary" loading={m.isPending} disabled={!changed} onClick={() => m.mutate({ ...(name.trim() !== draft.name ? { name: name.trim() } : {}), ...(slug !== draft.slug ? { slug } : {}) })}>Save settings</Button></div> : null}
      <SelectField label="Translation status" value={draft.translationStatus ?? 'DRAFT'} disabled={!canEdit} onChange={(e) => m.mutate({ translationStatus: e.target.value })} error={err('translationStatus')}
        hint="Approving a translation needs permission to publish. It becomes Published when the page is published.">
        {translationStatuses.map((s) => <option key={s} value={s} disabled={s === 'PUBLISHED' || (s === 'APPROVED' && !can('landings.publish'))}>{STATUS_LABEL[s]}</option>)}
      </SelectField>
      {draft.siblings?.length ? (
        <div>
          <p style={{ margin: '0 0 4px', fontWeight: 600 }}>Other languages</p>
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {draft.siblings.map((s) => <li key={s.variantId}><Link to={`/landings/${s.variantId}`}>{s.locale}</Link> <span className="ag-mono">/{s.slug}</span> · {STATUS_LABEL[s.translationStatus] ?? s.translationStatus}{s.published ? ' · live' : ''}</li>)}
          </ul>
        </div>
      ) : null}
      {m.isError ? <Notice tone="error" title="Could not save">{m.error.message}</Notice> : null}
    </div>
  );
}
